import {
  Button as AriaButton,
  type ButtonProps as AriaButtonProps,
  composeRenderProps,
} from 'react-aria-components';
import { tv, type VariantProps } from 'tailwind-variants';
import { focusRing } from '../utils';

export const buttonStyles = tv({
  base: [
    'inline-flex items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap',
    'transition-colors duration-150 ease-standard cursor-default select-none',
    'data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed',
    focusRing,
  ],
  variants: {
    variant: {
      primary: 'bg-accent text-fg-on-accent data-[hovered]:bg-accent-hover data-[pressed]:bg-accent-hover',
      secondary:
        'bg-surface text-fg border border-border-strong data-[hovered]:bg-sunken data-[pressed]:bg-sunken',
      ghost: 'bg-transparent text-fg data-[hovered]:bg-sunken data-[pressed]:bg-sunken',
      danger: 'bg-danger text-fg-on-accent data-[hovered]:opacity-90 data-[pressed]:opacity-90',
    },
    size: {
      sm: 'h-9 px-3 text-sm',
      md: 'min-h-touch px-4 text-base',
      lg: 'h-12 px-5 text-lg',
    },
    iconOnly: { true: 'px-0 aspect-square' },
  },
  defaultVariants: { variant: 'primary', size: 'md' },
});

export interface ButtonProps extends AriaButtonProps, VariantProps<typeof buttonStyles> {}

/** Accessible button (React Aria). Touch-sized by default (44px). */
export function Button({ variant, size, iconOnly, className, ...props }: ButtonProps) {
  return (
    <AriaButton
      {...props}
      className={composeRenderProps(className, (cn, renderProps) =>
        buttonStyles({ ...renderProps, variant, size, iconOnly, className: cn }),
      )}
    />
  );
}
